import {useEffect, useMemo, useState} from 'react';
import {useRouter} from 'next/router';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import FiltersIcon from '~assets/images/icons/ui/filters.svg';
import ArrowLeftIcon from '~assets/images/icons/ui/arrow-left.svg';
import {Text} from '~components/atoms/Text';
import ClearIcon from '~assets/images/icons/ui/close.svg';
import {PlotSearch} from '~molecules/PlotSearch';
import {Routes} from '~constants/routes';
import {useFilters} from '~providers/FiltersProvider';
import SmallPlotIcon from '~assets/images/icons/ui/small-plot-icon.svg';
import useFetchPartners from '~api/useFetchPartners';
import {filtersToQueryString} from '~utils/filters';
import Filters from '~molecules/FiltersMenu/Filters';

import {
    ClearFiltersBtn,
    CloseBtn,
    Container,
    ContainerUnderLogo,
    ContentWrapper,
    FixedRow,
    MaskedLogo,
    OpenBtn,
    PageTitle,
    ScrollWrapper,
    TopRow,
} from './styles';

type FiltersMenuProps = {
    alwaysOpenOnDesktop?: boolean;
    openBtnStyle: 'white' | 'black';
    pageTitle?: string;
    isList?: boolean;
};

export const FiltersMenu = ({
    alwaysOpenOnDesktop = false,
    openBtnStyle,
    pageTitle,
    isList,
}: FiltersMenuProps) => {
    const router = useRouter();
    const t = useScopeTranslation('common', 'filters');
    const {filters, setFilter, clearFilters, filtersWhitelist, emptyFilters} =
        useFilters();

    const [isOpen, setIsOpen] = useState<boolean | null>(() =>
        filtersWhitelist.length && window.innerWidth > 1088 ? true : null,
    );
    const {data: partners} = useFetchPartners(Boolean(isOpen));

    // Because of initial filters we can't just check if url has query
    const showClearFiltersBtn = useMemo(
        () => Boolean(filtersToQueryString(filters, filtersWhitelist)),
        [filters],
    );

    useEffect(() => {
        if (
            filters.status === 'sold' &&
            (filters.price.min !== emptyFilters.price.min ||
                filters.price.max !== emptyFilters.price.max)
        ) {
            setFilter('price', emptyFilters.price);
        }
    }, [filters]);

    const searchPartners = useMemo(() => {
        if (!partners || !filtersWhitelist.includes('partner')) {
            return undefined;
        }
        return partners;
    }, [partners, filtersWhitelist]);

    return (
        <Container isOpen={isOpen} alwaysOpenOnDesktop={alwaysOpenOnDesktop}>
            <MaskedLogo onClick={() => router.push(Routes.Index)} />
            <ContainerUnderLogo color={openBtnStyle} isList={isList}>
                {filtersWhitelist.length ? (
                    <OpenBtn onClick={() => setIsOpen(true)}>
                        <FiltersIcon />
                        <Text>{t('filters')}</Text>
                    </OpenBtn>
                ) : (
                    pageTitle && (
                        <PageTitle>
                            <SmallPlotIcon />
                            {pageTitle}
                        </PageTitle>
                    )
                )}
            </ContainerUnderLogo>

            <ContentWrapper>
                <ScrollWrapper>
                    <FixedRow>
                        <TopRow>
                            <CloseBtn onClick={() => setIsOpen(false)}>
                                <ArrowLeftIcon />
                                <Text>{t('filters')}</Text>
                            </CloseBtn>
                            {showClearFiltersBtn && (
                                <ClearFiltersBtn onClick={clearFilters}>
                                    <ClearIcon />
                                    <Text>{t('clearFilters')}</Text>
                                </ClearFiltersBtn>
                            )}
                        </TopRow>
                        <PlotSearch
                            handleSearch={(value) => setFilter('searchTerms', value)}
                            showPartnerLands={(value) => setFilter('partner', value)}
                            searchTerms={filters.searchTerms}
                            partners={searchPartners}
                            isList={isList}
                        />
                    </FixedRow>
                    <Filters partners={partners} isList={isList} />
                </ScrollWrapper>
            </ContentWrapper>
        </Container>
    );
};
